import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import { dataV2Api } from "apis/dataV2/dataV2Api";
import { procoreApi } from "apis/procore/procoreApi";
import { bottomToolbarReducer } from "components/toolbars/mobile/slice";
import { alignmentReducer } from "features/alignment/slice";
import { arcgisSlice } from "features/arcgis";
import { arcgisApi } from "features/arcgis/arcgisApi";
import { areaReducer } from "features/area";
import { bimCollabApi, bimCollabReducer } from "features/bimCollab";
import { bimTrackApi, bimTrackReducer } from "features/bimTrack";
import { bookmarksReducer } from "features/bookmarks";
import { clashReducer } from "features/clash";
import { clashApi } from "features/clash/clashApi";
import { coordinatesReducer } from "features/coordinates";
import { crossSectionReducer } from "features/crossSection/slice";
import { deviationsReducer } from "features/deviations";
import { ditioApi } from "features/ditio";
import { ditioReducer } from "features/ditio";
import { formsApi, formsReducer } from "features/forms";
import { groupsReducer } from "features/groups";
import { heightProfileReducer } from "features/heightProfile";
import { imagesReduces } from "features/images";
import { jiraApi, jiraReducer } from "features/jira";
import { manholeReducer } from "features/manhole";
import { markersReducer } from "features/markers";
import { measureReducer } from "features/measure";
import { miniMapReducer } from "features/miniMap";
import { mobileMeasureReducer } from "features/mobileMeasure";
import { modelTreeReducer } from "features/modelTree/slice";
import { myLocationReducer } from "features/myLocation";
import { offlineReducer } from "features/offline";
import { omega365Reducer } from "features/omega365";
import { orthoCamReducer } from "features/orthoCam";
import { clippingOutlineLaserReducer } from "features/outlineLaser";
import { pimsReducer } from "features/pims";
import { pointLineReducer } from "features/pointLine";
import { pointVisualizationReducer } from "features/pointVisualization";
import { propertiesReducer } from "features/properties/slice";
import { propertyTreeApi, propertyTreeReducer } from "features/propertyTree";
import { colorOverridesMemoryStats, renderReducer } from "features/render";
import { selectionBasketReducer } from "features/selectionBasket";
import { undoRedoReducer } from "features/undoRedo";
import { xsiteManageApi, xsiteManageReducer } from "features/xsiteManage";
import { authReducer } from "slices/authSlice";
import { explorerReducer } from "slices/explorer";
import { featureTogglesReducer } from "slices/featureToggles";
import { procoreUxReducer } from "slices/procoreUx";
import { toolbarVisibilityReducer } from "slices/toolbarVisibility";
import { registerMemoryStatsProvider } from "utils/memoryDiagnostics";

import { registerStore } from "./storeRegistry";

const rootReducer = combineReducers({
    explorer: explorerReducer,
    procoreUx: procoreUxReducer,
    render: renderReducer,
    auth: authReducer,
    groups: groupsReducer,
    bookmarks: bookmarksReducer,
    images: imagesReduces,
    forms: formsReducer,
    [formsApi.reducerPath]: formsApi.reducer,
    deviations: deviationsReducer,
    measure: measureReducer,
    area: areaReducer,
    orthoCam: orthoCamReducer,
    manhole: manholeReducer,
    pointLine: pointLineReducer,
    myLocation: myLocationReducer,
    bimCollab: bimCollabReducer,
    heightProfile: heightProfileReducer,
    clippingOutline: clippingOutlineLaserReducer,
    coordinates: coordinatesReducer,
    selectionBasket: selectionBasketReducer,
    properties: propertiesReducer,
    offline: offlineReducer,
    pims: pimsReducer,
    propertyTree: propertyTreeReducer,
    [propertyTreeApi.reducerPath]: propertyTreeApi.reducer,
    [bimCollabApi.reducerPath]: bimCollabApi.reducer,
    bimTrack: bimTrackReducer,
    [bimTrackApi.reducerPath]: bimTrackApi.reducer,
    ditio: ditioReducer,
    [ditioApi.reducerPath]: ditioApi.reducer,
    jira: jiraReducer,
    [jiraApi.reducerPath]: jiraApi.reducer,
    xsiteManage: xsiteManageReducer,
    [xsiteManageApi.reducerPath]: xsiteManageApi.reducer,
    [dataV2Api.reducerPath]: dataV2Api.reducer,
    [procoreApi.reducerPath]: procoreApi.reducer,
    arcgis: arcgisSlice.reducer,
    omega365: omega365Reducer,
    [arcgisApi.reducerPath]: arcgisApi.reducer,
    [clashApi.reducerPath]: clashApi.reducer,
    clash: clashReducer,
    pointVisualization: pointVisualizationReducer,
    crossSection: crossSectionReducer,
    alignment: alignmentReducer,
    modelTree: modelTreeReducer,
    undoRedo: undoRedoReducer,
    bottomToolbar: bottomToolbarReducer,
    markers: markersReducer,
    mobileMeasure: mobileMeasureReducer,
    miniMap: miniMapReducer,
    featureToggles: featureTogglesReducer,
    toolbarVisibility: toolbarVisibilityReducer,
});

export const createStore = () => {
    return configureStore({
        reducer: rootReducer,
        devTools: import.meta.env.NODE_ENV === "development",
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: {
                    ignoredActions: [
                        "undoRedo/push",
                        "undoRedo/undo",
                        "undoRedo/redo",
                        "markers/createCollection",
                        "markers/addMarker",
                        "markers/updateMarker",
                        "markers/updateCollectionConfig",
                        "markers/updateCreationConfig",
                        "miniMap/setDrawingData",
                        "bookmarks/setConfirmLargeBookmark",
                        // Plain data, but payloads can hold hundreds of thousands of object ids -
                        // deep-walking them in dev makes every dispatch crawl
                        "render/setObjectColor",
                        "render/clearObjectColor",
                    ],
                    ignoredPaths: [
                        "miniMap",
                        "undoRedo",
                        /^markers\.collections\..*\.config/,
                        /^markers\.collections\..*\.markerCreationConfig/,
                        "bookmarks.confirmLargeBookmark",
                        // Plain data, but can hold hundreds of thousands of keys (see above)
                        "render.colorOverrides",
                    ],
                    ignoredActionPaths: ["meta.arg", "meta.baseQueryMeta", "meta.view", "meta.publicIdsToObjectIds"],
                },
                immutableCheck: {
                    // Can hold hundreds of thousands of keys (see above)
                    ignoredPaths: ["render.colorOverrides"],
                },
            })
                .concat(bimCollabApi.middleware)
                .concat(bimTrackApi.middleware)
                .concat(ditioApi.middleware)
                .concat(formsApi.middleware)
                .concat(jiraApi.middleware)
                .concat(xsiteManageApi.middleware)
                .concat(dataV2Api.middleware)
                .concat(procoreApi.middleware)
                .concat(propertyTreeApi.middleware)
                .concat(arcgisApi.middleware)
                .concat(clashApi.middleware),
    });
};

export const store = createStore();

registerStore(store);

setupListeners(store.dispatch);

// Report the property tree's per-value id Records, which can reach scene-scale
// cardinality when a common property value is grouped. Registered only for the
// app's singleton store (not per-test stores).
registerMemoryStatsProvider("propertyTreeState", () => {
    const groupsState = store.getState().propertyTree.groups;
    let idCount = 0;
    for (const group of groupsState.groups) {
        // for-in count instead of Object.keys() to avoid materializing a huge
        // key array on scene-scale groups during a diagnostics reading.
        for (const _key in group.ids) {
            idCount++;
        }
    }
    return {
        property: groupsState.property,
        groupCount: groupsState.groups.length,
        idCount,
        // Identity Record<number, number> costs ~32 bytes/entry in V8
        // dictionary mode.
        approxBytes: idCount * 32,
    };
});

registerMemoryStatsProvider("colorOverrides", () => colorOverridesMemoryStats(store.getState().render.colorOverrides));

export type AppStore = typeof store;
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
